/**
 * Sawyer E4 (#754) — small pieces shared by the Team messages panels:
 * the composer, the time stamp, the skeletons, the error and stale lines.
 */
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { absoluteTime, MAX_BODY, relativeTime } from '../../lib/staff-messages-api.js';

/** Shown from 1,800 characters (AC3). */
export const COUNTER_FROM = 1800;

export function bodyState(text: string): { trimmed: string; over: boolean; empty: boolean; left: number } {
  const trimmed = text.trim();
  return { trimmed, over: trimmed.length > MAX_BODY, empty: trimmed.length === 0, left: MAX_BODY - trimmed.length };
}

export function TimeStamp({ iso }: { iso: string }): JSX.Element {
  const abs = absoluteTime(iso);
  return (
    <time className="ferro-tm__time" dateTime={iso} title={abs}>
      <span aria-hidden="true">{relativeTime(iso)}</span>
      <span className="ferro-tm__sr">{abs}</span>
    </time>
  );
}

/** Is this a touch-first device? There, Enter inserts a newline (AC21). */
function coarsePointer(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
}

export interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  submitLabel: string;
  placeholder: string;
  label: string;
  rows?: number;
  busy?: boolean;
  /** Enter sends on desktop (thread composer); the board composer is multi-line. */
  enterSends?: boolean;
  focusOnMount?: boolean;
  /** Extra controls left of the submit button (Tag a dog, the dog chip). */
  extra?: ReactNode;
  testId: string;
}

export function Composer({
  value,
  onChange,
  onSubmit,
  submitLabel,
  placeholder,
  label,
  rows = 4,
  busy = false,
  enterSends = false,
  focusOnMount = false,
  extra,
  testId,
}: ComposerProps): JSX.Element {
  const ref = useRef<HTMLTextAreaElement>(null);
  const { over, empty, left } = bodyState(value);
  const disabled = empty || over || busy;
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (!enterSends || e.key !== 'Enter' || e.shiftKey || coarsePointer()) return;
    e.preventDefault();
    if (!disabled) onSubmit();
  }

  return (
    <form
      className="ferro-tm-composer"
      data-testid={testId}
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled) onSubmit();
      }}
    >
      <label className="ferro-tm__sr" htmlFor={`${testId}-input`}>
        {label}
      </label>
      <textarea
        id={`${testId}-input`}
        ref={ref}
        className="ferro-tm-composer__input"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        data-testid={`${testId}-input`}
      />
      <div className="ferro-tm-composer__row">
        <div className="ferro-tm-composer__extra">{extra}</div>
        {value.trim().length >= COUNTER_FROM ? (
          <span
            className={`ferro-tm-composer__counter${over ? ' ferro-tm-composer__counter--over' : ''}`}
            data-testid={`${testId}-counter`}
            aria-live="polite"
          >
            {over ? `${(-left).toLocaleString()} over` : `${left.toLocaleString()} left`}
          </span>
        ) : null}
        <button
          type="submit"
          className="ferro-btn ferro-btn--primary ferro-tm-composer__submit"
          disabled={disabled}
          aria-busy={busy ? 'true' : undefined}
          data-testid={`${testId}-submit`}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

export function LoadError({ onRetry, testId }: { onRetry: () => void; testId: string }): JSX.Element {
  return (
    <div className="ferro-tm-error" role="alert" data-testid={testId}>
      <p>Couldn&apos;t load team messages. Check your connection and try again.</p>
      <button type="button" className="ferro-btn ferro-btn--outline" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/** Principle 5 stale state: the list stays, this line says it may be behind. */
export function StaleLine({ lastGood }: { lastGood: Date | null }): JSX.Element {
  const at = lastGood ? lastGood.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : null;
  return (
    <p className="ferro-tm-stale" role="status" data-testid="team-messages-stale">
      Couldn&apos;t refresh. Retrying.{at ? ` Last updated ${at}.` : ''}
    </p>
  );
}

export function SkeletonRows({ count, variant }: { count: number; variant: 'post' | 'thread' | 'bubble' }): JSX.Element {
  return (
    <div className="ferro-tm-skeletons" aria-busy="true" aria-label="Loading team messages" data-testid="team-messages-loading">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className={`ferro-tm-skeleton ferro-tm-skeleton--${variant}`} />
      ))}
    </div>
  );
}

/** Run `fn` every `ms` while the tab is visible; refresh on return. */
export function useVisiblePoll(fn: () => void, ms: number, enabled: boolean): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') fnRef.current();
    }, ms);
    const onVis = () => {
      if (document.visibilityState === 'visible') fnRef.current();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ms, enabled]);
}
